import { useEffect } from 'react';
import { Alert } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { useDebouncedValue } from '@s20/hooks/useDebouncedValue';
import { httpClient } from '@s20/services/httpClient';

/**
 * Checks, whenever the timesheets, contract or charge changes in `model`, that the destination contract can
 * bill what is being moved, and lists everything that is missing. Reports whether the move should be blocked.
 */
export const MoveContractCompatibilityAlert = ({ url, model, onBlockedChange }) => {
    const debouncedModel = useDebouncedValue(model, 500);

    const active = Boolean(model.contractId) && model.timesheetIds.length > 0;
    const enabled = Boolean(debouncedModel.contractId) && debouncedModel.timesheetIds.length > 0;

    const { data: problems = [], isLoading } = useQuery({
        queryKey: ['moveContractCompatibility', url, debouncedModel],
        queryFn: () => httpClient.post(url, debouncedModel),
        enabled,
    });

    // The last check no longer applies while the inputs are still changing.
    const blocked = active && (!isEqual(model, debouncedModel) || isLoading || problems.length > 0);

    useEffect(() => {
        onBlockedChange(blocked);
    }, [blocked, onBlockedChange]);

    if (!active || problems.length === 0) return null;

    return (
        <Alert
            color="danger"
            className="mt-3"
        >
            <strong>The destination contract isn&apos;t configured correctly:</strong>
            <ul className="mb-0">
                {problems.map((problem) => <li key={problem}>{problem}</li>)}
            </ul>
        </Alert>
    );
};

export default MoveContractCompatibilityAlert;
